/**
 * Customer-class resolution contract (plan §4.5).
 *
 * The effective customer class for a booking resolves with fall-through on
 * null, in strict precedence:
 *
 *   1. Booking-level override (operator sets a special class for one booking)
 *   2. Account-level class    (heavy-user tier on the account; H-3a / H-5)
 *   3. Organization (or unit) class (the org default; H-3b)
 *   4. System default class   (`customer_class.is_system_default = true`)
 *
 * H-3b ships steps 3 and 4 in the UI (org/unit class + system default). Steps
 * 1–2 are H-5; the helper already accepts them so H-5 slots in without rework.
 *
 * The function is intentionally pure and id-based (not entity-based): a
 * customer class is identified by its opaque id, mirroring how the backend
 * resolves it. Returns the first non-null id in precedence order, or null when
 * none is set (no system default configured yet — a misconfiguration the
 * caller surfaces, not a silent free pass).
 */

export interface CustomerClassResolutionInput {
    /** Operator override for a single booking. Highest precedence. */
    bookingClassId?: string | null;
    /** Heavy-user tier carried on the account (H-3a / H-5). */
    accountClassId?: string | null;
    /**
     * The org's default class — or the unit's override when the booking is
     * placed in a unit. The caller is responsible for collapsing
     * unit-over-org before passing it in (unit wins over org, both via
     * `customer_class_id`).
     */
    orgClassId?: string | null;
    /** The tenant's `is_system_default` class. Final fallback. */
    systemDefaultClassId?: string | null;
}

/**
 * Resolve the effective customer-class id from the precedence chain.
 *
 * @returns the first non-null/non-undefined id in
 *   booking > account > org > system-default order, or `null` if every level
 *   is null/undefined.
 */
export const resolveCustomerClassId = (
    input: CustomerClassResolutionInput,
): string | null => {
    const chain: Array<string | null | undefined> = [
        input.bookingClassId,
        input.accountClassId,
        input.orgClassId,
        input.systemDefaultClassId,
    ];
    for (const candidate of chain) {
        if (candidate !== null && candidate !== undefined) {
            return candidate;
        }
    }
    return null;
};

/**
 * Collapse a unit-level class over its org-level default. The unit wins when
 * it carries its own class; otherwise the org's default applies. Use this to
 * build the `orgClassId` argument for {@link resolveCustomerClassId}.
 */
export const resolveOrgOrUnitClassId = (
    orgClassId: string | null | undefined,
    unitClassId: string | null | undefined,
): string | null => {
    if (unitClassId !== null && unitClassId !== undefined) return unitClassId;
    if (orgClassId !== null && orgClassId !== undefined) return orgClassId;
    return null;
};
